import axios from 'axios';
import { 
  ApiResponse,
  LoggerProduct,
  LoggerProductFormData,
  LoggerCustomer,
  LoggerCustomerFormData,
  ConfigFileTemplate
} from '../types';

// Create axios instance with base URL
import { API_URL } from '../utils/ConfigUtils';
const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Add request interceptor for JWT token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Logger Product API
export const getLoggerProducts = async (): Promise<LoggerProduct[]> => {
  try {
    const response = await axiosInstance.get<LoggerProduct[]>('/logger/products');
    return response.data;
  } catch (error) {
    console.error('Error fetching logger products:', error);
    return [];
  }
};

export const getLoggerProduct = async (id: string): Promise<LoggerProduct | null> => {
  try {
    const response = await axiosInstance.get<LoggerProduct>(`/logger/products/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching logger product ${id}:`, error);
    return null;
  }
};

export const createLoggerProduct = async (data: LoggerProductFormData): Promise<ApiResponse<LoggerProduct>> => {
  try {
    const response = await axiosInstance.post<ApiResponse<LoggerProduct>>('/logger/products', data);
    return response.data;
  } catch (error) {
    console.error('Error creating logger product:', error);
    throw error;
  }
};

export const updateLoggerProduct = async (id: string, data: Partial<LoggerProductFormData>): Promise<ApiResponse<LoggerProduct>> => {
  try {
    const response = await axiosInstance.put<ApiResponse<LoggerProduct>>(`/logger/products/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating logger product ${id}:`, error);
    throw error;
  }
};

export const deleteLoggerProduct = async (id: string): Promise<ApiResponse<null>> => {
  try {
    const response = await axiosInstance.delete<ApiResponse<null>>(`/logger/products/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting logger product ${id}:`, error);
    throw error;
  }
};

// Logger Customer API
export const getLoggerCustomers = async (): Promise<LoggerCustomer[]> => {
  try {
    const response = await axiosInstance.get<LoggerCustomer[]>('/logger/customers');
    return response.data;
  } catch (error) {
    console.error('Error fetching logger customers:', error);
    return [];
  }
};

export const getLoggerCustomer = async (id: string): Promise<LoggerCustomer | null> => {
  try {
    const response = await axiosInstance.get<LoggerCustomer>(`/logger/customers/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching logger customer ${id}:`, error);
    return null;
  }
};

export const createLoggerCustomer = async (data: LoggerCustomerFormData): Promise<ApiResponse<LoggerCustomer>> => {
  try {
    const response = await axiosInstance.post<ApiResponse<LoggerCustomer>>('/logger/customers', data);
    return response.data;
  } catch (error) {
    console.error('Error creating logger customer:', error);
    throw error;
  }
};

export const updateLoggerCustomer = async (id: string, data: Partial<LoggerCustomerFormData>): Promise<ApiResponse<LoggerCustomer>> => {
  try {
    const response = await axiosInstance.put<ApiResponse<LoggerCustomer>>(`/logger/customers/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating logger customer ${id}:`, error);
    throw error;
  }
};

export const deleteLoggerCustomer = async (id: string): Promise<ApiResponse<null>> => {
  try {
    const response = await axiosInstance.delete<ApiResponse<null>>(`/logger/customers/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting logger customer ${id}:`, error);
    throw error;
  }
};

export const generateConfigFile = async (customerId: string, loggerType: string): Promise<ConfigFileTemplate> => {
  try {
    const response = await axiosInstance.get<ConfigFileTemplate>(`/logger/config`, {
      params: { 
        customer_id: customerId,
        logger_type: loggerType
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error generating config file for customer ${customerId}:`, error);
    throw error;
  }
}; 